import { UI_CONTROL_TIERS, HARVESTED_ROUTE_PATH_PATTERN, MAX_HARVESTED_ROUTE_PATH_CHARS } from './chunk-NOQNWA6J.js';
import { compileWordBuckets, foldTextForRiskCheck, decodePathForRiskCheck } from './chunk-DTQUYCTY.js';
import { z } from 'zod';

// src/ui-runtime/harvested-precache.ts
var MAX_HARVESTED_PRECACHE_ROUTES = 200;
var MAX_PRECACHE_CACHES_READ = 12;

// src/ui-runtime/harvested-well-known.ts
var MAX_HARVESTED_WELL_KNOWN_ROUTES = 200;

// src/ui-runtime/harvested-speculation.ts
var MAX_HARVESTED_SPECULATION_ROUTES = 200;

// src/ui-runtime/unsafe-path-words.ts
var ANCHORED_UNSAFE_PATH_WORDS = [
  // en — deletion, deactivation, session. Commerce deliberately absent.
  String.raw`delete|remove|destroy|erase|purge|wipe|truncate|revoke|deactivate|disable|reset|unsubscribe|unregister|deregister|uninstall|terminate|cancel|clear\s?all`,
  String.raw`sign\s?out|signout|log\s?out|logout|log\s?off|logoff|end\s?session`,
  // es
  String.raw`eliminar|borrar|quitar|suprimir|purgar|revocar|desactivar|restablecer|desinstalar|cancelar|dar\s?de\s?baja|cerrar\s?sesi[oó]n|salir`,
  // fr — accented AND ASCII spellings, because a PATH routinely drops accents.
  String.raw`supprimer|effacer|retirer|purger|r[ée]voquer|d[ée]sactiver|r[ée]initialiser|d[ée]sinstaller|r[ée]silier|annuler|(?:se\s?)?d[ée]sabonner|(?:se\s?)?d[ée]connecter|d[ée]connexion`,
  // de — with the oe/ue transliterations a `.de` path uses.
  String.raw`l[öo]schen|loeschen|entfernen|verwerfen|bereinigen|leeren|widerrufen|deaktivieren|deinstallieren|zur[üu]cksetzen|zuruecksetzen|k[üu]ndigen|kuendigen|abbestellen|abbrechen|stornieren|abmelden|ausloggen|abmeldung`,
  // pt
  String.raw`excluir|apagar|remover|revogar|desativar|redefinir|sair|terminar\s?sess[aã]o|encerrar\s?sess[aã]o|desconectar`,
  // it
  String.raw`elimina(?:re)?|rimuov(?:i|ere)|cancella(?:re)?|svuota(?:re)?|revoca(?:re)?|disattiva(?:re)?|reimposta(?:re)?|disinstalla(?:re)?|annulla(?:re)?|esci|disconnetti|disconnessione`,
  // nl — `verwijder(en)` and NOT the participle `verwijderd(e)`: the destructive
  // list carries it and accepts firing on "Verwijderde items" as one extra
  // confirmation; on a PATH that label is a LISTING (`/verwijderde-items`) and
  // refusing it costs the page.
  String.raw`verwijder(?:en)?|wissen|legen|intrekken|deactiveren|opnieuw\s?instellen|de[ïi]nstalleren|opzeggen|annuleren|uitloggen|afmelden`,
  // sv / da / no — one block; the stems differ by a letter. `t[öø]m` and never
  // a bare `o`: `tom` is the name Tom on every English page.
  String.raw`radera|ta\s?bort|slet(?:t)?|fjern|t[öø]m|[aå]terkalla|inaktivera|[aå]terst[aä]ll|avinstallera|s[aä]g\s?upp|avbryt|annuller|avbestill|logga\s?ut|logg\s?ut|log\s?ud|logg\s?av`,
  // vi
  String.raw`x[oó]a|xo[aá]|g[oỡ]\s?b[oỏ]|lo[aạ]i\s?b[oỏ]|thu\s?h[oồ]i|v[oô]\s?hi[eệ]u\s?h[oó]a|[dđ][aặ]t\s?l[aạ]i|h[uủ]y|[dđ][aă]ng\s?xu[aâấ]t|tho[aá]t`,
  // tl
  String.raw`burahin|tanggalin|alisin|kanselahin|mag-?log\s?out`,
  // tr — enumerated suffixes, never a bare `sil` stem: as a stem it fired on
  // the whole English `sil-` family (`Silver`, `Silent`, `Silicon`).
  // Without the `-miş` participle the destructive list enumerates: `Silinmiş
  // öğeler` is a deleted-items VIEW, and `/silinmis-ogeler` is its route.
  String.raw`sil(?:i?n(?:di|ir|sin|ecek|iz|me|e)?|di|er|ecek|iyor|me(?:k|si|di)?|sin)?`,
  String.raw`kald[iı]r(?:[iı]l(?:d[iı]|s[iı]n|acak)?|d[iı]|acak|ma)?|temizle(?:n(?:di|sin|ecek)?|di|yin)?|s[iı]f[iı]rla(?:n(?:d[iı]|s[iı]n)?|y[iı]n)?`,
  String.raw`iptal|devre\s?d[iı][sş][iı]|[cç][iı]k[iı][sş](?:\s?yap)?|oturumu\s?kapat`,
  // pl — the cancel and session words are full forms; the deletion stems are
  // in bucket 2, because Polish inflects by suffix.
  String.raw`anuluj|anulowa[cć]|wyloguj(?:\s?si[eę])?|wylogowanie`,
  // cs / sk
  String.raw`zru[sš]it|zru[sš]te|odhl[aá]sit(?:\s?se)?|odhl[aá]senie|odhl[aá][sš]en[ií]`,
  // ro
  String.raw`anuleaz[aă]|anulare|deconectare|deconecteaz[aă]|ie[sș]ire\s?din\s?cont`,
  // fi
  String.raw`peruuta|kirjaudu\s?ulos|uloskirjautuminen`,
  // hu
  String.raw`m[eé]gse|kijelentkez[eé]s|kil[eé]p[eé]s`,
  // hr / sr / sl / bs
  String.raw`otka[zž]i|odjava|odjavi(?:te)?\s?se`,
  // ru / uk / bg — the SESSION words as full forms. `выход` is a full form
  // here on purpose: as a stem it is `выходные` (weekends).
  String.raw`вы[йх]ти|выход|вийти|вихід|изход|отмена|отменить|скасувати|отказ`,
  // el — full forms for cancel and session; deletion stems are in bucket 2.
  String.raw`ακ[ύυ]ρωση|ακυρ[ώω]στε|αποσύνδεση|αποσυνδεση`,
  // hi
  String.raw`लॉग\s?आउट|साइन\s?आउट`
];
var PREFIX_STEM_UNSAFE_PATH_WORDS = [
  // ru / uk / bg
  String.raw`удал|видал|изтри|сотри|стере|очист|отозв|отключ|деактивир|сброс|скинут`,
  // el — accented AND unaccented, because Greek moves the accent as it inflects.
  String.raw`διαγρα|διαγρά|διάγρα|αφαίρεσ|αφαιρεσ|αφαιρώ|εκκαθάρισ|ανάκλησ|απενεργοποί|επαναφορ`,
  // hi
  String.raw`हटा|मिटा|निकाल|रद्द`,
  // fi
  String.raw`poista|tyhjenn|nollaa`,
  // hu
  String.raw`t[oö]rl|t[oö]r[oö]l|elt[aá]vol[ií]t|[uü]r[ií]t|visszavon|deaktiv[aá]l|lemond`,
  // pl
  String.raw`usu[nń]|usuw|skasu|wyczy[sś]|wycofa|dezaktywu|zresetu|odinstalu`,
  // cs / sk
  String.raw`smaz|sma[zž]|odstra[nň]|odstr[aá][nň]|vyma[zž]|vypr[aá]zdn|deaktivova|resetova|odinstalova`,
  // ro
  String.raw`[sș]terg|elimin[aă]|revoc|dezactiv|reseteaz|dezinstal`,
  // hr / sr / sl / bs
  String.raw`obri[sš]|izbri[sš]|ukloni|isprazn|opozov|deaktivir|resetira|deinstalira`
];
var BARE_UNSAFE_PATH_WORDS = [
  // id / ms
  String.raw`hapus|padam|kosongkan|nonaktifkan|copot\s?pemasangan|batal|keluar|log\s?keluar`,
  // ar / fa
  String.raw`حذف|إزال|ازال|مسح|تعطيل|[إا]لغاء|لغو|پاک|تسجيل\s?الخروج|تسجيل\s?خروج|خروج`,
  // he
  String.raw`מחק(?!ר)|מחיק|הסר|הסיר|השבת|ביטול|בטל|התנתק|יציאה`,
  // ja
  String.raw`削除|消去|抹消|解約|退会|無効化|取り消し|ログアウト|サインアウト`,
  // zh-Hans / zh-Hant
  String.raw`删除|刪除|移除|清空|清除|停用|重置|取消|注销|註銷|退出登[录錄]|登出`,
  // ko
  String.raw`삭제|제거|비우기|해지|탈퇴|비활성화|초기화|취소|로그아웃`,
  // th
  String.raw`ลบ|ล้าง|ปิดใช้งาน|รีเซ็ต|ยกเลิก|ออกจากระบบ|ลงชื่อออก`
];
var UNSAFE_PATH_RE = compileWordBuckets({
  anchored: ANCHORED_UNSAFE_PATH_WORDS,
  prefixStem: PREFIX_STEM_UNSAFE_PATH_WORDS,
  bare: BARE_UNSAFE_PATH_WORDS
});
function foldPathForRefusal(path) {
  return foldTextForRiskCheck(decodePathForRiskCheck(path));
}
function namesUnsafePath(path) {
  return UNSAFE_PATH_RE.test(foldPathForRefusal(path));
}

// src/ui-runtime/same-site-origin.ts
function stripLeadingWww(hostname) {
  return hostname.startsWith("www.") ? hostname.slice("www.".length) : hostname;
}
function isSameSiteOrigin(a, b) {
  if (a === "" || b === "" || a === "null" || b === "null") return false;
  if (a === b) return true;
  let ua;
  let ub;
  try {
    ua = new URL(a);
    ub = new URL(b);
  } catch {
    return false;
  }
  if (ua.protocol !== ub.protocol || ua.port !== ub.port) return false;
  const ha = stripLeadingWww(ua.hostname);
  const hb = stripLeadingWww(ub.hostname);
  return ha !== "" && ha === hb;
}
function isSiblingSiteOrigin(candidate, walked) {
  if (isSameSiteOrigin(candidate, walked)) return false;
  if (candidate === "" || walked === "" || candidate === "null" || walked === "null") return false;
  let uc;
  let uw;
  try {
    uc = new URL(candidate);
    uw = new URL(walked);
  } catch {
    return false;
  }
  if (uc.protocol !== uw.protocol || uc.port !== uw.port) return false;
  const base = stripLeadingWww(uw.hostname);
  if (!base.includes(".")) return false;
  return uc.hostname.endsWith(`.${base}`);
}

// src/ui-runtime/page-path.ts
var IDENTIFIER_SEGMENT = /^(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,}|(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{8,})$/i;
function templateSegment(segment) {
  return segment.includes("@") || IDENTIFIER_SEGMENT.test(segment) ? ":id" : segment;
}
function templatePath(pathname) {
  if (pathname === "" || pathname === "/") return pathname;
  return pathname.split("/").map((segment) => segment === "" ? segment : templateSegment(segment)).join("/");
}
function isPageShapedPath(pathname) {
  const last = pathname.split("/").pop() ?? "";
  if (last === "" || !last.includes(".")) return true;
  return /\.html?$/i.test(last);
}
function canonicalizePagePath(pathname) {
  const trimmed = pathname.replace(/\/(?:index\.html?)?$/i, "");
  return trimmed === "" ? "/" : trimmed;
}

// src/ui-runtime/soft-not-found.ts
var AXES = ["bytes", "words", "lines"];
var MIN_MISSING_SAMPLES = 2;
var SOFT_NOT_FOUND_PROBE_PATHS = [
  "/.syncanix-probe-a11f2c",
  "/.syncanix-probe-93de70/b"
];
function documentShape(body) {
  if (body === "") return { bytes: 0, words: 0, lines: 0 };
  const words = body.split(/\s+/).filter((w) => w !== "").length;
  return {
    bytes: new TextEncoder().encode(body).length,
    words,
    lines: body.split("\n").filter((l) => l !== "").length
  };
}
function calibrateSoftNotFound(samples) {
  if (samples.missing.length < MIN_MISSING_SAMPLES) return null;
  if (samples.present.length === 0) return null;
  for (const axis2 of AXES) {
    const first = samples.missing[0]?.[axis2];
    if (first === void 0) continue;
    if (!samples.missing.every((s) => s[axis2] === first)) continue;
    if (samples.present.some((s) => s[axis2] === first)) continue;
    return { axis: axis2, value: first };
  }
  return null;
}
function isSoftNotFound(filter, shape) {
  return filter !== null && shape[filter.axis] === filter.value;
}

// src/ui-runtime/structure-simhash.ts
var SHINGLE_K = 2;
var BITS = 32;
function fnv1a(text) {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
function simhashFromTagPath(tagPath) {
  return simhashFromTokens(tagPath);
}
function simhashFromTokens(tokens) {
  if (tokens.length === 0) return "";
  const lowered = tokens.map((t) => t.toLowerCase());
  const shingles = [];
  for (let i = 0; i <= lowered.length - SHINGLE_K; i += 1) {
    shingles.push(lowered.slice(i, i + SHINGLE_K).join(">"));
  }
  if (shingles.length === 0) shingles.push(lowered.join(">"));
  const weights = new Array(BITS).fill(0);
  for (const shingle of shingles) {
    const h = fnv1a(shingle);
    for (let bit = 0; bit < BITS; bit += 1) {
      weights[bit] = (weights[bit] ?? 0) + (h >>> bit & 1 ? 1 : -1);
    }
  }
  let out = 0;
  for (let bit = 0; bit < BITS; bit += 1) {
    if ((weights[bit] ?? 0) > 0) out |= 1 << bit;
  }
  return (out >>> 0).toString(16).padStart(8, "0");
}
function stableDigest(text) {
  const forward = fnv1a(text).toString(16).padStart(8, "0");
  const reversed = fnv1aFrom(text, 2538058380);
  return forward + reversed.toString(16).padStart(8, "0");
}
function fnv1aFrom(text, basis) {
  let hash = basis >>> 0;
  for (let i = text.length - 1; i >= 0; i -= 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
function hammingSimilarity(a, b) {
  if (a === "" || b === "" || a.length !== b.length) return 0;
  let matching = 0;
  const total = a.length * 4;
  for (let i = 0; i < a.length; i += 1) {
    const xa = Number.parseInt(a[i] ?? "", 16);
    const xb = Number.parseInt(b[i] ?? "", 16);
    if (Number.isNaN(xa) || Number.isNaN(xb)) return 0;
    let x = xa ^ xb;
    while (x > 0) {
      matching -= x & 1;
      x >>= 1;
    }
    matching += 4;
  }
  return matching / total;
}

// src/ui-runtime/bind-evidence.ts
var GROUP_SIMILARITY = 0.95;
function stringVerdict(a, b) {
  if (a === "" || b === "") return "unknown";
  return a === b ? "agree" : "conflict";
}
function identityVerdict(o, c) {
  return o.signature === c.signature && o.locale === c.locale ? "agree" : "unknown";
}
function placementVerdict(o, c) {
  const page = stringVerdict(o.page, c.page);
  if (page === "conflict") return "conflict";
  if (page !== "agree") return "unknown";
  if (stringVerdict(o.sectionId, c.sectionId) === "agree") return "agree";
  return hammingSimilarity(o.structureSimhash, c.structureSimhash) >= GROUP_SIMILARITY ? "agree" : "unknown";
}
function ordinalVerdict(o, c) {
  const a = o.siblingOrdinal ?? -1;
  const b = c.siblingOrdinal ?? -1;
  if (a < 0 || b < 0) return "unknown";
  return a === b ? "agree" : "conflict";
}
function evaluateEvidence(o, c) {
  const verdicts = {
    identity: identityVerdict(o, c),
    placement: placementVerdict(o, c),
    wire: stringVerdict(o.wireName, c.wireName),
    anchor: stringVerdict(o.anchorText ?? "", c.anchorText ?? ""),
    position: ordinalVerdict(o, c)
  };
  const families = Object.keys(verdicts);
  return {
    verdicts,
    corroboration: families.filter((f) => f !== "position" && verdicts[f] === "agree").length,
    conflicts: families.filter((f) => verdicts[f] === "conflict")
  };
}
function isRefusedByConflict(o, c) {
  return stringVerdict(o.wireName, c.wireName) === "conflict";
}
function nonPositionalVouches(o, only, tied) {
  const unique = (value, read) => value !== "" && tied.filter((c) => read(c) === value).length === 1;
  if (o.wireName !== "" && o.wireName === only.wireName && unique(o.wireName, (c) => c.wireName)) {
    return true;
  }
  const anchor = o.anchorText ?? "";
  return anchor === (only.anchorText ?? "") && unique(anchor, (c) => c.anchorText ?? "");
}

// src/ui-runtime/bind-decision.ts
var WEIGHT_SECTION = 0.3;
var WEIGHT_PAGE = 0.2;
var WEIGHT_STRUCTURE = 0.35;
var WEIGHT_WIRE_NAME = 0.15;
var BIND_THRESHOLD = 0.6;
var SECTIONLESS_BIND_THRESHOLD = 0.5;
var TIE_MARGIN = 0.1;
var simhashSimilarity = hammingSimilarity;
function weightedScore(o, c) {
  let score = 0;
  if (o.sectionId !== "" && o.sectionId === c.sectionId) score += WEIGHT_SECTION;
  if (o.page !== "" && o.page === c.page) score += WEIGHT_PAGE;
  score += WEIGHT_STRUCTURE * simhashSimilarity(o.structureSimhash, c.structureSimhash);
  if (o.wireName !== "" && o.wireName === c.wireName) score += WEIGHT_WIRE_NAME;
  return score;
}
function decideBinding(observation, candidates) {
  const exact = candidates.find(
    (c) => c.signature === observation.signature && c.locale === observation.locale
  );
  if (exact !== void 0) {
    return {
      outcome: "bind",
      identityId: exact.identityId,
      score: 1,
      reason: "signature",
      evidence: evaluateEvidence(observation, exact)
    };
  }
  if (candidates.length === 0) return { outcome: "mint", reason: "no-candidates" };
  const localeCompatible = (c) => c.locale === "" || observation.locale === "" || c.locale === observation.locale;
  const weighable = candidates.filter(
    (c) => localeCompatible(c) && c.role === observation.role && !isRefusedByConflict(observation, c)
  );
  if (weighable.length === 0) {
    const localeBlocked = candidates.some((c) => !localeCompatible(c));
    return { outcome: "mint", reason: localeBlocked ? "locale-change" : "below-threshold" };
  }
  const thresholdFor = (c) => observation.sectionId === "" && c.sectionId === "" ? SECTIONLESS_BIND_THRESHOLD : BIND_THRESHOLD;
  const scored = weighable.map((c) => ({ id: c.identityId, score: weightedScore(observation, c), candidate: c })).sort((a, b) => b.score - a.score);
  const best = scored[0];
  if (best === void 0 || best.score < thresholdFor(best.candidate)) {
    return { outcome: "mint", reason: "below-threshold" };
  }
  const rival = scored[1];
  if (rival !== void 0 && best.score - rival.score < TIE_MARGIN && rival.score >= thresholdFor(rival.candidate)) {
    const tied = scored.filter(
      (s) => best.score - s.score < TIE_MARGIN && s.score >= thresholdFor(s.candidate)
    );
    const oOrdinal = observation.siblingOrdinal ?? -1;
    if (oOrdinal >= 0) {
      const positioned = tied.filter(
        (s) => hammingSimilarity(observation.structureSimhash, s.candidate.structureSimhash) >= GROUP_SIMILARITY && (s.candidate.siblingOrdinal ?? -1) === oOrdinal
      );
      const only = positioned[0];
      if (positioned.length === 1 && only !== void 0 && nonPositionalVouches(
        observation,
        only.candidate,
        tied.map((s) => s.candidate)
      )) {
        return {
          outcome: "bind",
          identityId: only.id,
          score: only.score,
          reason: "weighted",
          evidence: evaluateEvidence(observation, only.candidate)
        };
      }
    }
    return {
      outcome: "abstain",
      candidateIds: tied.map((s) => s.id),
      scores: tied.map((s) => s.score),
      evidence: tied.map((s) => evaluateEvidence(observation, s.candidate))
    };
  }
  return {
    outcome: "bind",
    identityId: best.id,
    score: best.score,
    reason: "weighted",
    evidence: evaluateEvidence(observation, best.candidate)
  };
}

// src/ui-runtime/page-lang.ts
var PAGE_LANG_PATTERN = /^[a-z]{2,3}(-[a-z0-9]{1,8})*$/;
var MAX_PAGE_LANG_CHARS = 35;
function parsePageLang(raw) {
  if (typeof raw !== "string") return "";
  const trimmed = raw.trim().toLowerCase();
  if (trimmed === "" || trimmed.length > MAX_PAGE_LANG_CHARS) return "";
  return PAGE_LANG_PATTERN.test(trimmed) ? trimmed : "";
}

// src/ui-runtime/route-template.ts
var UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var HEX_ID_SEGMENT = /^[0-9a-f]{16,}$/i;
var NUMERIC_SEGMENT = /^\d+$/;
var SHORT_HEX_ID_SEGMENT = /^(?=[0-9a-f]*\d)[0-9a-f]{8,15}$/i;
var ULID_SEGMENT = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/i;
var KSUID_SEGMENT = /^[A-Za-z0-9]{27}$/;
var NANOID_SEGMENT = /^[A-Za-z0-9_-]{21}$/;
var PREFIXED_ID_SEGMENT = /^[a-z][a-z0-9]{0,11}_([A-Za-z0-9]{8,})$/;
function looksOpaque(token) {
  if (!/[a-z]/.test(token) || !/[A-Z]/.test(token)) return false;
  let digits = 0;
  for (const ch of token) if (ch >= "0" && ch <= "9") digits += 1;
  return digits >= 2;
}
function looksOpaqueTail(token) {
  const mixedCase = /[a-z]/.test(token) && /[A-Z]/.test(token);
  return mixedCase && (/\d/.test(token) || token.length >= 12) || looksOpaque(token);
}
function isIdentifierSegment(segment) {
  if (segment.includes("@")) return true;
  if (NUMERIC_SEGMENT.test(segment)) return true;
  if (HEX_ID_SEGMENT.test(segment) || SHORT_HEX_ID_SEGMENT.test(segment)) return true;
  if (ULID_SEGMENT.test(segment)) return true;
  if (KSUID_SEGMENT.test(segment) && looksOpaque(segment)) return true;
  if (NANOID_SEGMENT.test(segment) && looksOpaque(segment)) return true;
  const prefixed = PREFIXED_ID_SEGMENT.exec(segment);
  return prefixed !== null && looksOpaqueTail(prefixed[1] ?? "");
}
var CANONICAL_PARAM = ":param";
var PLACEHOLDER_SEGMENT = /^:[A-Za-z][A-Za-z0-9_]*$/;
function clusterPathTemplate(path) {
  const cleaned = path.split("?")[0]?.split("#")[0]?.replace(/\/$/, "") ?? "";
  const segments = cleaned.split("/").filter((s) => s.length > 0);
  const out = segments.map((segment) => {
    if (UUID_SEGMENT.test(segment)) return ":uuid";
    return isIdentifierSegment(segment) ? ":id" : segment;
  });
  return `/${out.join("/")}`;
}
function normalizeRouteTemplate(template) {
  return template.split("/").map((segment) => PLACEHOLDER_SEGMENT.test(segment) ? CANONICAL_PARAM : segment).join("/");
}
function routeTemplatesMatch(a, b) {
  if (a === "" || b === "") return false;
  return normalizeRouteTemplate(a) === normalizeRouteTemplate(b);
}

// src/ui-runtime/route-template-fill.ts
var MAX_ROUTE_PARAM_VALUE_CHARS = 128;
var PARAM_VALUE_PATTERN = /^[\p{L}\p{M}\p{N}._~!'()-]+$/u;
var ALL_DOTS = /^\.+$/;
var CATCH_ALL_SEGMENT = "*";
function refuse(reason) {
  return { ok: false, reason };
}
function routeTemplateParams(template) {
  const names2 = [];
  for (const segment of template.split("/")) {
    if (!PLACEHOLDER_SEGMENT.test(segment)) continue;
    const name = segment.slice(1);
    if (!names2.includes(name)) names2.push(name);
  }
  return names2;
}
function isFillableValue(value) {
  return typeof value === "string" && value.length > 0 && value.length <= MAX_ROUTE_PARAM_VALUE_CHARS && !ALL_DOTS.test(value) && PARAM_VALUE_PATTERN.test(value);
}
function fillRouteTemplate(template, params) {
  if (!HARVESTED_ROUTE_PATH_PATTERN.test(template)) return refuse("template-invalid");
  const segments = template.split("/");
  if (segments.includes(CATCH_ALL_SEGMENT)) return refuse("catch-all");
  const needed = routeTemplateParams(template);
  for (const supplied of Object.keys(params)) {
    if (!needed.includes(supplied)) return refuse("unknown-param");
  }
  const filled = [];
  for (const segment of segments) {
    if (!PLACEHOLDER_SEGMENT.test(segment)) {
      filled.push(segment);
      continue;
    }
    const name = segment.slice(1);
    if (!Object.hasOwn(params, name)) return refuse("missing-param");
    const value = params[name];
    if (!isFillableValue(value)) return refuse("value-invalid");
    filled.push(encodeURIComponent(value));
  }
  const path = filled.join("/");
  if (path.length > MAX_HARVESTED_ROUTE_PATH_CHARS) return refuse("result-invalid");
  if (!HARVESTED_ROUTE_PATH_PATTERN.test(path)) return refuse("result-invalid");
  return { ok: true, path };
}

// src/ui-runtime/regex-witness.ts
function splitTopLevel(source) {
  const parts = [];
  let depth = 0;
  let current = "";
  let inClass = false;
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i] ?? "";
    if (ch === "\\") {
      current += ch + (source[i + 1] ?? "");
      i += 1;
      continue;
    }
    if (inClass) {
      current += ch;
      if (ch === "]") inClass = false;
      continue;
    }
    if (ch === "[") {
      inClass = true;
      current += ch;
      continue;
    }
    if (ch === "(") depth += 1;
    if (ch === ")") depth -= 1;
    if (ch === "|" && depth === 0) {
      parts.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts.filter((p) => p !== "");
}
function sampleOf(source) {
  let out = "";
  let i = 0;
  while (i < source.length) {
    const ch = source[i] ?? "";
    if (ch === "\\") {
      const next = source[i + 1] ?? "";
      if (next === "s") {
        out += " ";
        i += 2;
        if (source[i] === "?" || source[i] === "+" || source[i] === "*") i += 1;
        continue;
      }
      if (next === "p" || next === "P") {
        const close = source.indexOf("}", i + 2);
        if (close === -1) throw new Error(`unterminated property escape in ${source}`);
        out += "a";
        i = close + 1;
        if (source[i] === "?" || source[i] === "+" || source[i] === "*") i += 1;
        continue;
      }
      out += next;
      i += 2;
      continue;
    }
    if (ch === "[") {
      const end = source.indexOf("]", i);
      if (end === -1) throw new Error(`unterminated class in ${source}`);
      const body = source.slice(i + 1, end);
      const dash = body.indexOf("-");
      if (dash > 0 && dash < body.length - 1) {
        throw new Error(`range class not supported: [${body}] in ${source}`);
      }
      out += body[0] ?? "";
      i = end + 1;
      if (source[i] === "?" || source[i] === "+" || source[i] === "*") i += 1;
      continue;
    }
    if (ch === "(") {
      let depth = 0;
      let end = -1;
      for (let j = i; j < source.length; j += 1) {
        const c = source[j];
        if (c === "\\") {
          j += 1;
          continue;
        }
        if (c === "(") depth += 1;
        if (c === ")") {
          depth -= 1;
          if (depth === 0) {
            end = j;
            break;
          }
        }
      }
      if (end === -1) throw new Error(`unterminated group in ${source}`);
      let body = source.slice(i + 1, end);
      const quantified = source[end + 1] === "?";
      i = end + (quantified ? 2 : 1);
      if (body.startsWith("?!") || body.startsWith("?=")) continue;
      if (body.startsWith("?:")) body = body.slice(2);
      out += sampleOf(splitTopLevel(body)[0] ?? "");
      continue;
    }
    if (ch === "?" || ch === "+" || ch === "*") {
      i += 1;
      continue;
    }
    out += ch;
    i += 1;
  }
  return out;
}

// src/ui-runtime/navigable-target.ts
var MAX_NAVIGABLE_TARGET_CHARS = 1e3;
function isNavigableTarget(target) {
  if (typeof target !== "string") return false;
  const trimmed = target.trim();
  if (trimmed === "" || trimmed.length > MAX_NAVIGABLE_TARGET_CHARS) return false;
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return false;
  if (trimmed.includes("\\") || trimmed.includes(":")) return false;
  return true;
}
var PLACEHOLDER_SEGMENT2 = /^:[A-Za-z0-9_]+$/;
var CATCH_ALL_SEGMENT2 = "*";
function isNavigableRouteTemplate(template) {
  if (template === "" || !template.startsWith("/")) return false;
  if (template.length > MAX_NAVIGABLE_TARGET_CHARS) return false;
  return template.split("/").every(
    (segment) => segment !== CATCH_ALL_SEGMENT2 && (PLACEHOLDER_SEGMENT2.test(segment) || isNavigableSegment(segment))
  );
}
function isNavigableSegment(segment) {
  return !segment.includes(":") && !segment.includes("\\");
}
function isNavigableAbsoluteTarget(target, approvedOrigins) {
  if (typeof target !== "string" || approvedOrigins.length === 0) return false;
  const trimmed = target.trim();
  if (trimmed === "" || trimmed.length > MAX_NAVIGABLE_TARGET_CHARS) return false;
  if (trimmed.includes("\\")) return false;
  let url;
  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  if (url.username !== "" || url.password !== "") return false;
  return approvedOrigins.some((origin) => {
    let approved;
    try {
      approved = new URL(origin);
    } catch {
      return false;
    }
    return approved.origin === url.origin;
  });
}

// src/ui-runtime/payment-surface-hosts.ts
var PAYMENT_SCRIPT_HOSTS = [
  // Card processors and their checkout SDKs.
  "stripe.com",
  "stripe.network",
  "paypal.com",
  "paypalobjects.com",
  "braintreegateway.com",
  "braintree-api.com",
  "adyen.com",
  "checkout.com",
  "squareup.com",
  "squarecdn.com",
  "worldpay.com",
  "globalpay.com",
  "bluesnap.com",
  "2checkout.com",
  "authorize.net",
  "nmi.com",
  "payu.com",
  "razorpay.com",
  "paytm.in",
  "mercadopago.com",
  "ebanx.com",
  "iyzipay.com",
  "paddle.com",
  "fastspring.com",
  "lemonsqueezy.com",
  "chargebee.com",
  "recurly.com",
  // Wallets and pay-later, which move money on the same page.
  "klarna.com",
  "klarnaservices.com",
  "afterpay.com",
  "clearpay.co.uk",
  "affirm.com",
  "alipay.com",
  "alipayobjects.com",
  "pay.google.com",
  "applepay.cdn-apple.com",
  // Regional processors with meaningful web presence.
  "mollie.com",
  "sofort.com",
  "yookassa.ru",
  "tinkoff.ru",
  "toss.im",
  "kakaopay.com",
  "linepay.me",
  "veritrans.co.id",
  "midtrans.com",
  "flutterwave.com",
  "paystack.com"
];
function isPaymentScriptHost(hostname) {
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (host === "") return false;
  return PAYMENT_SCRIPT_HOSTS.some((known) => host === known || host.endsWith(`.${known}`));
}
function pageLoadsPaymentScript(srcs, baseUrl) {
  return srcs.some((src) => {
    try {
      return isPaymentScriptHost(new URL(src, baseUrl).hostname);
    } catch {
      return false;
    }
  });
}
var MAX_ACTION_EVIDENCE = 8;
var ActionEvidenceSchema = /* @__PURE__ */ z.discriminatedUnion("kind", [
  /** The page must observably change in some way. The weakest expectation. */
  z.object({ kind: z.literal("dom-change") }).strict(),
  /** A region with this accessible name must become visible. */
  z.object({ kind: z.literal("reveal"), name: z.string().min(1).max(160) }).strict(),
  /** The route must become this template (`:param` segments match any one segment). */
  z.object({ kind: z.literal("navigation"), routeTemplate: z.string().min(1).max(400) }).strict(),
  /** A request matching this `METHOD /path` template must go out and be accepted. */
  z.object({ kind: z.literal("request"), endpointTemplate: z.string().min(1).max(400) }).strict()
]);
var ActionEvidenceListSchema = /* @__PURE__ */ z.array(ActionEvidenceSchema).max(MAX_ACTION_EVIDENCE);
function parseActionEvidence(raw) {
  const parsed = ActionEvidenceListSchema.safeParse(raw);
  return parsed.success ? parsed.data : void 0;
}
var EVIDENCE_OUTCOMES = ["confirmed", "no-evidence", "contradicted"];
function fold(name) {
  return name.replace(/\s+/g, " ").trim().toLowerCase();
}
function names(list) {
  return new Set(list.map(fold));
}
function routeMatches(template, path) {
  const want = template.split("/");
  const got = path.split("/");
  if (want.length !== got.length) return false;
  return want.every((seg, i) => seg.startsWith(":") || seg === got[i]);
}
function judgeOne(want, saw) {
  switch (want.kind) {
    case "dom-change":
      return saw.domChanged ? "confirmed" : "no-evidence";
    case "reveal": {
      const wanted = fold(want.name);
      if (names(saw.revealedNames).has(wanted)) return "confirmed";
      if (names(saw.vanishedNames).has(wanted)) return "contradicted";
      return "no-evidence";
    }
    case "navigation":
      if (routeMatches(want.routeTemplate, saw.routeAfter)) return "confirmed";
      if (saw.routeAfter !== saw.routeBefore) return "contradicted";
      return "no-evidence";
    case "request": {
      const matching = saw.requests.filter(
        (r) => r.endpointTemplate.toLowerCase() === want.endpointTemplate.toLowerCase()
      );
      if (matching.length === 0) return "no-evidence";
      return matching.every((r) => r.ok) ? "confirmed" : "contradicted";
    }
  }
}
function judgeActionEvidence(expected, saw) {
  const failed = expected.filter((want) => judgeOne(want, saw) === "contradicted");
  if (failed.length > 0) return { outcome: "contradicted", failed };
  if (expected.length === 0) return { outcome: "no-evidence", failed: [] };
  const allConfirmed = expected.every((want) => judgeOne(want, saw) === "confirmed");
  return { outcome: allConfirmed ? "confirmed" : "no-evidence", failed: [] };
}
var PAGE_ACT_EFFECTS = ["none", "write", "destructive"];
var PAGE_ACT_GATES = ["run", "ask", "off"];
var MAX_PAGE_ACT_CONTROLS = 2e3;
var PageActControlPolicySchema = /* @__PURE__ */ z.object({ effect: z.enum(PAGE_ACT_EFFECTS), gate: z.enum(PAGE_ACT_GATES) }).strict();
var PageActPolicySchema = /* @__PURE__ */ z.object({
  /** The Advanced workspace dial (Z-6) — every page act asks. Also what the read-only posture derives. */
  askAlways: z.boolean(),
  /** Keyed by the control's `controlId` (the widget's own signature). Absent ⇒ the walk never saw it write. */
  controls: z.record(z.string().min(1).max(128), PageActControlPolicySchema).refine((c) => Object.keys(c).length <= MAX_PAGE_ACT_CONTROLS, {
    message: "page-act policy exceeds the control ceiling"
  }),
  /**
   * F5 — what the walk observed each control DO, so an act can be verified
   * after the fact. Keyed by the same `controlId` as `controls` above,
   * because it is the same join: `controls` says whether pressing needs to
   * ask, this says what should be observable once it has been pressed.
   *
   * A SEPARATE map rather than a field on the policy above, because the two
   * are not co-extensive: a control the walk saw reveal a panel has an
   * expectation and no write policy at all, and forcing them into one record
   * would either invent a policy for it or lose the expectation.
   *
   * OPTIONAL, and that is a wire decision rather than a convenience: this
   * object is `.strict()`, so a required field would refuse every block a
   * server built before this shipped — costing a workspace its whole page-act
   * policy over a field it never had. Absent means "nothing was learned",
   * which reads as `no-evidence`.
   */
  evidence: z.record(z.string().min(1).max(128), ActionEvidenceListSchema).refine((e) => Object.keys(e).length <= MAX_PAGE_ACT_CONTROLS, {
    message: "page-act evidence exceeds the control ceiling"
  }).optional()
}).strict();
function parsePageActPolicy(raw) {
  const parsed = PageActPolicySchema.safeParse(raw);
  return parsed.success ? parsed.data : void 0;
}
var TIER_RANK = {
  reads: UI_CONTROL_TIERS.indexOf("reads"),
  writes: UI_CONTROL_TIERS.indexOf("writes"),
  destructive: UI_CONTROL_TIERS.indexOf("destructive")
};
var MUTATES_DATA = /* @__PURE__ */ new Set([
  "write",
  "destructive"
]);
function decidePageAct(input) {
  if (!input.mutating) return "run";
  const policy = input.policy;
  const joined = policy === void 0 || input.controlId === void 0 ? void 0 : policy.controls[input.controlId];
  const widgetSaysMutating = MUTATES_DATA.has(input.riskClass);
  const serverSaysNone = joined?.effect === "none";
  const mutatesData = joined !== void 0 ? joined.effect !== "none" : widgetSaysMutating;
  if (joined?.gate === "off") return "deny";
  if (!mutatesData && !widgetSaysMutating) return policy?.askAlways === true ? "ask" : "run";
  if (input.tier === "reads") return "deny";
  if (serverSaysNone && input.riskClass === "destructive") {
    return TIER_RANK[input.tier] >= TIER_RANK.destructive ? "ask" : "deny";
  }
  if (joined?.gate === "ask") return "ask";
  if (serverSaysNone) return policy?.askAlways === true ? "ask" : "run";
  if (input.witnessed === true && policy !== void 0 && joined === void 0 && input.riskClass !== "destructive") {
    return policy.askAlways ? "ask" : "run";
  }
  if (policy === void 0) {
    if (input.riskClass === "destructive") {
      return TIER_RANK[input.tier] >= TIER_RANK.destructive ? "ask" : "deny";
    }
    return "ask";
  }
  if (joined !== void 0) {
    if (joined.effect === "destructive") {
      return TIER_RANK[input.tier] >= TIER_RANK.destructive ? "ask" : "deny";
    }
    return policy.askAlways ? "ask" : "run";
  }
  return "ask";
}
var MAX_CONTROL_AXIS_CHARS = 120;
var MAX_CONTROL_SIBLING_INDEX = 500;
var UNSTABLE_TOKEN = [
  // React 18 `useId` — `:r0:`, `:r1a:`, and the `«r0»` spelling React 19 emits.
  /^[:«]r[0-9a-z]+[:»]$/iu,
  // styled-components / linaria — `sc-` or `css-` plus a hash.
  // The lookahead is what separates a hash from a word: `sc-a1b2c3` is generated and
  // `sc-header` is a class somebody wrote, and at this length the only thing that tells
  // them apart is that a hash carries a digit.
  /^(?:sc|css)-(?=[0-9a-z]*[0-9])[0-9a-z]{4,}$/iu,
  // Emotion's serialized name.
  /^css-[0-9a-z]+-[0-9a-zA-Z]+$/u,
  // CSS Modules — `File_local__hash`, hash at the end.
  /^[A-Za-z][\w$]*_[\w$]+__[0-9a-z]{4,}$/iu,
  // Angular view encapsulation.
  /^_ng(?:content|host)-[0-9a-z-]+$/iu,
  // Vue scoped styles.
  /^data-v-[0-9a-f]{6,}$/iu,
  // A bare hex or base36 run long enough to be generated rather than chosen.
  /^[0-9a-f]{8,}$/iu,
  // Radix and Headless UI generated ids.
  /^(?:radix|headlessui)-[0-9a-z:-]+$/iu
];
function isUnstableToken(token) {
  return UNSTABLE_TOKEN.some((pattern) => pattern.test(token));
}
function stableTokens(raw) {
  if (raw === null) return void 0;
  const kept = raw.trim().split(/\s+/u).filter((token) => token !== "" && !isUnstableToken(token));
  if (kept.length === 0) return void 0;
  return kept.join(" ").slice(0, MAX_CONTROL_AXIS_CHARS);
}
function stableId(raw) {
  if (raw === null || raw === "") return void 0;
  if (isUnstableToken(raw)) return void 0;
  return raw.slice(0, MAX_CONTROL_AXIS_CHARS);
}
function controlAxisDigest(text) {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}
var axis = /* @__PURE__ */ z.string().min(1).max(MAX_CONTROL_AXIS_CHARS);
var ControlFingerprintSchema = /* @__PURE__ */ z.object({
  /** The ARIA role, computed rather than read — the axis a semantic rewrite breaks. */
  role: axis.optional(),
  /** The accessible name. A copy change or a translation breaks it; a restyle does not. */
  name: axis.optional(),
  /** `data-testid` / `data-test` / `data-cy` — the one axis authors write FOR this. */
  testId: axis.optional(),
  /** The element's `id`, when a person chose it (see {@link stableId}). */
  domId: axis.optional(),
  /** Stable class tokens, generated ones removed (see {@link stableTokens}). */
  classes: axis.optional(),
  /** Structural path from the document root, framework-generated tokens stripped. */
  path: axis.optional(),
  /** Position among element siblings, or absent past {@link MAX_CONTROL_SIBLING_INDEX}. */
  siblingIndex: z.number().int().min(0).max(MAX_CONTROL_SIBLING_INDEX).optional(),
  /** Digest of the enclosing row's identifying text — never the text (R-4). */
  rowDigest: axis.optional(),
  /** Rounded viewport box, as `x,y,w,h`. Absent where layout is unavailable. */
  box: axis.optional(),
  /** Which framework rendered this, when the element says so. */
  engine: axis.optional()
}).strict();
var CONTROL_FINGERPRINT_AXES = Object.keys(
  ControlFingerprintSchema.shape
);
function parseControlFingerprint(value) {
  const parsed = ControlFingerprintSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

// src/ui-runtime/control-match.ts
var CONTROL_MATCH_FLOOR = 0.72;
var CONTROL_MATCH_MARGIN = 0.05;
var BOX_NEAR_PX = 24;
var BOX_FAR_PX = 200;
var MIN_COMPARABLE_WEIGHT = 8;
var WEIGHTS = {
  testId: 10,
  domId: 8,
  name: 7,
  rowDigest: 6,
  path: 3,
  classes: 2,
  siblingIndex: 2,
  role: 2,
  box: 1
};
var WEIGHTED_AXES = Object.keys(WEIGHTS);
function boxDistance(a, b) {
  const pa = a.split(",").map(Number);
  const pb = b.split(",").map(Number);
  if (pa.length !== 4 || pb.length !== 4) return null;
  if (pa.some(Number.isNaN) || pb.some(Number.isNaN)) return null;
  const cx = (p) => (p[0] ?? 0) + (p[2] ?? 0) / 2;
  const cy = (p) => (p[1] ?? 0) + (p[3] ?? 0) / 2;
  return Math.hypot(cx(pa) - cx(pb), cy(pa) - cy(pb));
}
function axisAgreement(axis2, want, got) {
  if (axis2 !== "box") return want === got ? 1 : 0;
  const distance = boxDistance(String(want), String(got));
  if (distance === null) return 0;
  if (distance <= BOX_NEAR_PX) return 1;
  if (distance >= BOX_FAR_PX) return 0;
  return 0.5;
}
function scoreControlFingerprint(stored, candidate) {
  if (stored.role !== void 0 && candidate.role !== void 0 && stored.role !== candidate.role) {
    return { score: 0, disqualified: "role", comparedAxes: [] };
  }
  if (stored.engine !== void 0 && candidate.engine !== void 0 && stored.engine !== candidate.engine) {
    return { score: 0, disqualified: "engine", comparedAxes: [] };
  }
  const comparedAxes = [];
  let total = 0;
  let earned = 0;
  for (const axis2 of WEIGHTED_AXES) {
    const want = stored[axis2];
    const got = candidate[axis2];
    if (want === void 0 || got === void 0) continue;
    comparedAxes.push(axis2);
    total += WEIGHTS[axis2];
    earned += WEIGHTS[axis2] * axisAgreement(axis2, want, got);
  }
  if (total < MIN_COMPARABLE_WEIGHT) {
    return { score: 0, disqualified: "too-little-evidence", comparedAxes };
  }
  return { score: earned / total, comparedAxes };
}
function matchControlFingerprint(stored, candidates) {
  let best = null;
  let runnerUp = 0;
  for (const entry of candidates) {
    const { score } = scoreControlFingerprint(stored, entry.candidate);
    if (best === null || score > best.score) {
      if (best !== null) runnerUp = best.score;
      best = { value: entry.value, score };
    } else if (score > runnerUp) {
      runnerUp = score;
    }
  }
  if (best === null || best.score < CONTROL_MATCH_FLOOR) return { kind: "no-match" };
  if (best.score - runnerUp < CONTROL_MATCH_MARGIN) {
    return { kind: "ambiguous", best: best.score, runnerUp };
  }
  return { kind: "matched", value: best.value, score: best.score };
}

export { ANCHORED_UNSAFE_PATH_WORDS, ActionEvidenceListSchema, ActionEvidenceSchema, BARE_UNSAFE_PATH_WORDS, CONTROL_FINGERPRINT_AXES, CONTROL_MATCH_FLOOR, CONTROL_MATCH_MARGIN, ControlFingerprintSchema, EVIDENCE_OUTCOMES, MAX_ACTION_EVIDENCE, MAX_CONTROL_AXIS_CHARS, MAX_CONTROL_SIBLING_INDEX, MAX_HARVESTED_PRECACHE_ROUTES, MAX_HARVESTED_SPECULATION_ROUTES, MAX_HARVESTED_WELL_KNOWN_ROUTES, MAX_NAVIGABLE_TARGET_CHARS, MAX_PAGE_ACT_CONTROLS, MAX_PRECACHE_CACHES_READ, MAX_ROUTE_PARAM_VALUE_CHARS, PAGE_ACT_EFFECTS, PAGE_ACT_GATES, PAYMENT_SCRIPT_HOSTS, PLACEHOLDER_SEGMENT, PREFIX_STEM_UNSAFE_PATH_WORDS, PageActControlPolicySchema, PageActPolicySchema, SOFT_NOT_FOUND_PROBE_PATHS, UNSAFE_PATH_RE, calibrateSoftNotFound, canonicalizePagePath, clusterPathTemplate, controlAxisDigest, decideBinding, decidePageAct, documentShape, evaluateEvidence, fillRouteTemplate, foldPathForRefusal, hammingSimilarity, isNavigableAbsoluteTarget, isNavigableRouteTemplate, isNavigableTarget, isPageShapedPath, isPaymentScriptHost, isRefusedByConflict, isSameSiteOrigin, isSiblingSiteOrigin, isSoftNotFound, isUnstableToken, judgeActionEvidence, matchControlFingerprint, namesUnsafePath, nonPositionalVouches, normalizeRouteTemplate, pageLoadsPaymentScript, parseActionEvidence, parseControlFingerprint, parsePageActPolicy, parsePageLang, routeTemplateParams, routeTemplatesMatch, sampleOf, scoreControlFingerprint, simhashFromTagPath, simhashFromTokens, splitTopLevel, stableDigest, stableId, stableTokens, templatePath, templateSegment };
//# sourceMappingURL=chunk-CE5XPGEA.js.map
//# sourceMappingURL=chunk-CE5XPGEA.js.map